import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import KundaliChart from "../components/KundaliChart";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import PaywallGate from "../components/PaywallGate";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useAuth } from "../context/AuthContext";
import { useToolUnlock } from "../lib/toolUnlock";
import {
  Sparkle, Coins, ArrowLeft, Calculator, ChartLineUp, Star,
  Lightning, TreeStructure, MedalMilitary, Circle, MagnifyingGlass, User,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const HOUSE_META = {
  h2: { label: "2nd · Hoard", subtitle: "savings, family wealth, speech" },
  h5: { label: "5th · Punya",  subtitle: "past-life merit, speculation gains" },
  h9: { label: "9th · Fortune", subtitle: "bhagya, mentors, big luck" },
  h11:{ label: "11th · Gains",  subtitle: "income, network, marketplace" },
};

const BAND_TONE = {
  strong: "bg-emerald-100 text-emerald-900 border-emerald-300",
  moderate: "bg-amber-100 text-amber-900 border-amber-300",
  weak: "bg-rose-100 text-rose-900 border-rose-300",
};

const DIGNITY_TONE = {
  exalted: "bg-emerald-600 text-white",
  mooltrikona: "bg-emerald-500 text-white",
  own_sign: "bg-lime-500 text-white",
  neutral: "bg-stone-300 text-stone-800",
  debilitated: "bg-rose-500 text-white",
};

export default function WealthHoraPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const { user } = useAuth();

  // Celebrity DB — for researching D2s of famous people. Loaded once.
  const [celebs, setCelebs] = useState([]);
  const [celebQuery, setCelebQuery] = useState("");
  const [celebSearchOpen, setCelebSearchOpen] = useState(false);
  const [pickedCeleb, setPickedCeleb] = useState(null);

  useEffect(() => {
    // Fetch all relevant celebrity categories in parallel and merge.
    // Wealth-adjacent categories only: billionaires, rags-to-riches AND
    // lottery-winners (added 2026-08-05 per user request — every AA-rated
    // lottery winner in the DB is highly instructive for D2 study since
    // their entire wealth arc is a single visible event).
    Promise.all([
      api.get("/celebrities/list/billionaires").catch(() => ({ data: { celebrities: [] } })),
      api.get("/celebrities/list/rags-to-riches").catch(() => ({ data: { celebrities: [] } })),
      api.get("/celebrities/list/lottery-winners").catch(() => ({ data: { celebrities: [] } })),
    ]).then(([a, b, c]) => {
      const merged = [
        ...(a?.data?.celebrities || []),
        ...(b?.data?.celebrities || []),
        ...(c?.data?.celebrities || []),
      ];
      // De-dupe by slug (in case future categories overlap) and stable-sort by name.
      const bySlug = new Map();
      merged.forEach((c) => { if (!bySlug.has(c.slug)) bySlug.set(c.slug, c); });
      setCelebs(Array.from(bySlug.values()).sort((x, y) => x.name.localeCompare(y.name)));
    });
  }, []);

  const filteredCelebs = useMemo(() => {
    const q = celebQuery.trim().toLowerCase();
    if (!q) return celebs.slice(0, 12);
    return celebs.filter((c) =>
      c.name.toLowerCase().includes(q)
      || (c.industry || "").toLowerCase().includes(q)
      || (c.country || "").toLowerCase().includes(q)
      || (c.category || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [celebs, celebQuery]);

  const loadCelebrity = async (c) => {
    if (!c) return;
    // The summary list only carries `dob`. Fetch full birth details
    // (time, place, tz) via the lightweight birth endpoint before
    // populating the form.
    try {
      const { data } = await api.get(`/celebrities/birth/${c.slug}`);
      setForm({
        date: data.dob,
        time: data.time || "12:00",
        place: data.place || "",
        timezone: data.tz || "Asia/Kolkata",
      });
      setPickedCeleb({ name: data.name, category: data.category, country: data.country, industry: data.industry });
      setResult(null);
      setSubmitted(false);
      if (!data.place) {
        toast.warning(`Loaded ${data.name}. Add birth-place manually — not on record.`);
      } else {
        toast.success(`Loaded ${data.name}'s birth chart. Compute below to see their D2 Hora.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load celebrity birth details.");
    }
  };

  // Server-side paywall: /wealth-hora/compute now requires an unlock for
  // this chart. Mirror the check on the frontend so we don't even fire the
  // compute call before the customer has paid.
  const hasBirth = !!(form.date && form.time && form.place);
  const { unlocked: alreadyUnlocked } = useToolUnlock({
    tool: "wealth_hora",
    birthDetails: hasBirth ? form : null,
    user,
  });
  const canView = unlocked || alreadyUnlocked;

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const runCompute = async () => {
    if (busy || result) return;
    setBusy(true);
    try {
      const { data } = await api.post("/wealth-hora/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='wh-result']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your Wealth Hora.");
    } finally {
      setBusy(false);
    }
  };

  // Compute only after unlock. No math, no chart, until paid.
  useEffect(() => {
    if (submitted && canView && !result && !busy) runCompute();
  }, [submitted, canView]);

  const analyze = (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setSubmitted(true);
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="wealth-hora-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center gap-1 text-sm text-muted mb-4"
          data-testid="wh-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Coins size={14} weight="duotone" /> Premium · D2 Wealth Hora
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          D2 Hora · <span className="italic text-gold">Wealth Calculator</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-3xl font-light">
          Your 2nd-house divisional chart — the kutumba (family wealth), speech, savings and self-earned assets ledger.
          Unlike the classical Parashari D2 (which forces every planet into just Cancer or Leo),
          this AstroKuber variant produces a full 12-house wealth chart so you get real, actionable insight
          across all zodiac axes — not just two.
        </p>

        {/* CELEBRITY DB SEARCH */}
        {celebs.length > 0 && (
          <div className="mt-6 max-w-3xl relative" data-testid="wh-celeb-search-wrap">
            <label className="label-eyebrow text-xs flex items-center gap-1.5 text-gold">
              <User size={12} weight="duotone" /> Or research a celebrity&apos;s D2 Hora
            </label>
            <div className="mt-1.5 relative">
              <MagnifyingGlass size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/60" />
              <input
                type="text"
                value={celebQuery}
                onFocus={() => setCelebSearchOpen(true)}
                onChange={(e) => { setCelebQuery(e.target.value); setCelebSearchOpen(true); }}
                onBlur={() => setTimeout(() => setCelebSearchOpen(false), 180)}
                placeholder="Search — Ambani, Jobs, Musk, Buffett…"
                className="w-full pl-9 pr-3 py-2.5 rounded-md border border-soft bg-bg text-sm"
                data-testid="wh-celeb-search"
              />
              {celebSearchOpen && filteredCelebs.length > 0 && (
                <div
                  className="absolute z-40 left-0 right-0 top-full mt-1 max-h-[320px] overflow-y-auto rounded-md shadow-2xl bg-white border border-amber-200"
                  data-testid="wh-celeb-results"
                >
                  {filteredCelebs.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); loadCelebrity(c); setCelebQuery(c.name); setCelebSearchOpen(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-start gap-2 border-b border-soft/50"
                      data-testid={`wh-celeb-opt-${c.slug}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#3F2D1B] truncate">
                          {c.name}
                          <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-mono uppercase">
                            {c.category?.replace("-", " ") || ""}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted truncate">
                          {c.industry || c.country} · born {c.dob}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {celebSearchOpen && filteredCelebs.length === 0 && celebQuery && (
                <div className="absolute z-40 left-0 right-0 top-full mt-1 rounded-md shadow-2xl bg-white border border-amber-200 px-3 py-4 text-xs text-muted text-center">
                  No celebrity matches &quot;{celebQuery}&quot;.
                </div>
              )}
            </div>
            {pickedCeleb && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-xs text-amber-900" data-testid="wh-celeb-picked">
                <User size={12} weight="fill" /> Loaded: <b>{pickedCeleb.name}</b>
                <button type="button" onClick={() => { setPickedCeleb(null); setForm({ date: "", time: "", place: "", timezone: "Asia/Kolkata" }); setResult(null); setSubmitted(false); }} className="ml-2 text-amber-700 hover:text-amber-900" data-testid="wh-celeb-clear">✕</button>
              </div>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4 max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                data-testid="wh-date"/>
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                data-testid="wh-time"/>
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Start typing a city — e.g. Mumbai, Delhi, London"
                  testId="wh-place"
                />
              </div>
            </label>
          </div>
          <button type="submit" disabled={busy}
            className="btn-primary rounded-md w-full sm:w-auto px-6 py-2.5 mt-2 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="wh-submit">
            <Calculator size={16} weight="duotone" />
            {busy ? "Computing…" : "Compute my D2 Wealth Chart"}
          </button>
        </form>

        {/* Standalone paywall — submitted but not yet unlocked. Nothing is
            computed or shown until payment lands. */}
        {submitted && !canView && (
          <div className="mt-10 max-w-3xl" data-testid="wh-paywall-only">
            <PaywallGate
              tool="wealth_hora"
              toolLabel="D2 Hora · Wealth Calculator"
              birthDetails={form}
              variant="standalone"
              onUnlocked={() => {
                setUnlocked(true);
                toast.success("Unlocked! Your D2 Wealth Chart is being generated…");
              }}
            />
          </div>
        )}

        {/* Loading state — after unlock, before compute finishes */}
        {submitted && canView && busy && !result && (
          <div className="mt-10 text-center animate-pulse text-muted text-sm" data-testid="wh-loading">
            Casting your D2 Wealth Hora chart…
          </div>
        )}

        {/* Result — only when unlocked & compute finished */}
        {result && canView && (
          <div className="mt-10 space-y-7" data-testid="wh-result">
            {/* HERO */}
            <section className="rounded-2xl overflow-hidden border border-amber-200 bg-gradient-to-br from-[#FFF8E7] via-[#FFF1D6] to-[#FFE7C2] p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-7 items-center">
                <div className="flex justify-center md:justify-start">
                  <div className="p-3 rounded-xl bg-white shadow-sm border border-amber-200">
                    <KundaliChart kundali={result.d2} size={300} />
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow text-amber-800 flex items-center gap-1.5">
                    <Coins size={12} weight="duotone" /> Your D2 Wealth Hora
                  </div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl mt-2 text-amber-900 leading-tight">
                    {result.d2_lagna_flavour.title}
                  </h2>
                  <div className="text-xs uppercase tracking-wider mt-1 text-amber-700">
                    D2 Lagna · {result.d2.lagna.rashi} · ruled by {result.d2.lagna.rashi_lord}
                  </div>
                  <p className="text-sm text-amber-900/90 mt-3 leading-relaxed">
                    {result.d2_lagna_flavour.read}
                  </p>
                </div>
              </div>
            </section>

            {/* 2ND HOUSE DEEP READ */}
            <section className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                <Star size={14} weight="duotone" /> The 2nd house · your wealth core
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TwoHCard title="D1 · Natal 2nd House" data={result.second_house.d1_2h} />
                <TwoHCard title="D2 · Wealth-Chart 2nd House" data={result.second_house.d2_2h} accent />
              </div>
            </section>

            {/* WEALTH AXIS 2·5·9·11 */}
            <section className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                <ChartLineUp size={14} weight="duotone" /> Wealth axis on D2 · 2 · 5 · 9 · 11
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {["h2","h5","h9","h11"].map((k) => (
                  <AxisCard key={k} h={result.wealth_axis[k]} meta={HOUSE_META[k]}/>
                ))}
              </div>
            </section>

            {/* ARCHETYPES */}
            {result.archetypes?.length > 0 && (
              <section className="card-surface rounded-2xl p-5 sm:p-7">
                <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                  <MedalMilitary size={14} weight="duotone" /> Your wealth archetype{result.archetypes.length > 1 ? "s" : ""}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {result.archetypes.map((a) => (
                    <div key={a.key} className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                      <div className="text-sm font-semibold text-amber-900">{a.title}</div>
                      <p className="text-xs text-amber-900/80 mt-1.5 leading-relaxed">{a.detail}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* PLANET DETAIL — Table */}
            <section className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                <TreeStructure size={14} weight="duotone" /> Every planet in your D2
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="text-left text-muted border-b border-soft">
                      <th className="py-2 pr-3">Planet</th>
                      <th className="py-2 pr-3">D2 sign</th>
                      <th className="py-2 pr-3">House (from D2 lagna)</th>
                      <th className="py-2 pr-3">Dignity</th>
                      <th className="py-2 pr-3">Hora</th>
                      <th className="py-2 pr-3">Wealth focus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result.d2._detail || {}).map(([name, d]) => (
                      <tr key={name} className="border-b border-soft/50 align-top">
                        <td className="py-2 pr-3 font-semibold">{name}</td>
                        <td className="py-2 pr-3">{d.d2_rashi} · <span className="text-muted">{d.d2_rashi_lord}</span></td>
                        <td className="py-2 pr-3">{d.d2_house}</td>
                        <td className="py-2 pr-3">
                          <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${DIGNITY_TONE[d.dignity] || DIGNITY_TONE.neutral}`}>
                            {d.dignity.replace("_"," ")}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`text-[10px] ${d.hora_kind === "own_hora" ? "text-emerald-800" : "text-indigo-800"}`}>
                            {d.hora_kind === "own_hora" ? "own · earned" : "gains · received"}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-muted">{d.wealth_flavour}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* REMEDIES */}
            <section className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                <Sparkle size={14} weight="duotone" /> How to activate your wealth · remedies
              </div>
              <p className="text-xs text-muted mb-4">
                Rules-based prescriptions for the planet lord of your weakest wealth-axis house
                (typically the 2nd or 11th on your D2). Try one remedy at a time for 40–90 days
                and observe what shifts.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {result.remedies?.map((r) => (
                  <div key={r.planet} className="rounded-lg border border-soft bg-white/60 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Circle size={10} weight="fill" className="text-gold" />
                      <div className="font-semibold text-sm">{r.planet}</div>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      {r.mantra && <p><span className="font-semibold text-[#3F2D1B]">Mantra:</span> {r.mantra}</p>}
                      {r.gem && <p><span className="font-semibold text-[#3F2D1B]">Gem:</span> {r.gem}</p>}
                      {r.charity && <p><span className="font-semibold text-[#3F2D1B]">Charity:</span> {r.charity}</p>}
                      {r.habit && <p><span className="font-semibold text-[#3F2D1B]">Daily habit:</span> {r.habit}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted mt-3 italic">
                Always trial a gem for 3 nights before permanent wear — remove if you experience sleep disturbance or nightmares.
              </p>
            </section>

            {/* PRACTICAL SUGGESTIONS */}
            <section className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                <Lightning size={14} weight="duotone" /> Practical wealth activation
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-[#3F2D1B] mb-2">
                    Business & income ideas
                  </h4>
                  <p className="text-[11px] text-muted mb-2">
                    Aligned to your strongest D2 planet · <b>{result.practical_suggestions.primary_business_seed}</b>
                  </p>
                  <ul className="space-y-1.5 text-xs text-[#3F2D1B]">
                    {result.practical_suggestions.business_ideas.map((idea, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-gold">›</span>
                        <span>{idea}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-[#3F2D1B] mb-2">
                    Savings & investment vehicles
                  </h4>
                  <p className="text-[11px] text-muted mb-2">
                    Fitted to your D2 2nd-house sign · <b>{result.second_house.d2_2h.sign}</b>
                  </p>
                  <ul className="space-y-1.5 text-xs text-[#3F2D1B]">
                    {result.practical_suggestions.savings_vehicles.map((v, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-gold">›</span>
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-soft">
                <h4 className="text-sm font-semibold text-[#3F2D1B] mb-2">
                  Daily & weekly wealth habits
                </h4>
                <ul className="space-y-1.5 text-xs text-[#3F2D1B]">
                  {result.practical_suggestions.habits.map((h, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-gold">›</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* METHOD NOTE */}
            <section className="text-[11px] text-muted italic max-w-3xl">
              {result.method_note}
            </section>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function TwoHCard({ title, data, accent }) {
  return (
    <div className={`rounded-lg p-4 border ${accent ? "border-amber-300 bg-amber-50/60" : "border-soft bg-white/60"}`}>
      <div className="text-[10px] label-eyebrow text-muted">{title}</div>
      <div className="mt-1.5 font-serif-display text-lg text-[#3F2D1B]">{data.sign}</div>
      <div className="text-xs text-muted">Lord · {data.lord}</div>
      {data.occupants?.length > 0 && (
        <div className="text-xs mt-1">Occupants: <b className="text-[#3F2D1B]">{data.occupants.join(", ")}</b></div>
      )}
      {data.lord_placed_in_house > 0 && (
        <div className="text-xs mt-0.5">Lord placed in house <b>{data.lord_placed_in_house}</b> ({data.lord_placed_in_sign})</div>
      )}
      <p className="text-xs text-[#3F2D1B]/80 mt-2 leading-relaxed">{data.one_liner}</p>
    </div>
  );
}

function AxisCard({ h, meta }) {
  return (
    <div className={`rounded-lg border p-3 ${BAND_TONE[h.band]}`}>
      <div className="text-[10px] label-eyebrow">{meta.label}</div>
      <div className="text-[10px] opacity-80">{meta.subtitle}</div>
      <div className="mt-2 flex items-end gap-1.5">
        <div className="text-2xl font-serif-display leading-none">{h.score}</div>
        <div className="text-[10px] uppercase tracking-wider pb-0.5">/10 · {h.band}</div>
      </div>
      <div className="text-[10px] mt-2">
        Lord <b>{h.lord}</b> in house <b>{h.lord_house || "?"}</b>
      </div>
      {h.occupants?.length > 0 && (
        <div className="text-[10px] mt-0.5">Occupants: {h.occupants.join(", ")}</div>
      )}
    </div>
  );
}
